import { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { getRackModel } from "../../../../modelPaths"
import { RackOptions, ShelfSelection } from "../../../../RackTypes"
import { addPlateStorage } from "../../../../utils/plate-storage"
import { computeShelfOffsets } from "../../../../utils/shelf-offsets"
import { HYBRID_DIVIDER_SHELF, HYBRID_LONG_PIN_PLATE_COUNT, HYBRID_PLATE_CENTER_RISE, HYBRID_PLATE_THICKNESS } from "../hybridConstants"
import { hybridShelfSpacing } from "../hybridShelfSpacing"
import { FrontCrossmemberBuilder } from "../../../rack/builders/frontCrossmemberBuilder"

// Same 1-5 shelf keys getShelfSelections fills, in slot order.
const HYBRID_SHELF_KEYS = ['shelf1', 'shelf2', 'shelf3', 'shelf4', 'shelf5'] as const

// Hybrid's upright mesh shares Blender tooling with Cave's and only exposes these 5 real sockets - no hybrid-specific slot names exist.
const HYBRID_SHELF_SLOTS = ['cave-shelf-one', 'cave-shelf-two', 'cave-shelf-three', 'cave-shelf-four', 'cave-shelf-five']

// SHARED_HYBRID_SHELVES builds a kettlebell/dumbbell shelf as the medball tray with an overlay dropped into its own same-named slot, never as a standalone mesh - the overlay exports carry no anchor of their own, so alone they sit on the upright instead of spanning off it. Slot name and mesh prefix match, so one map covers both.
const SHELF_OVERLAY: Record<string, string> = {
  'kettlebell shelf' : 'kb',
  'dumbbell shelf'   : 'db',
}

type ShelfGear = { slot: string, meshes: string[], count: Record<string, number> }

// The staged gear each shelf type holds under "View With Gear" - a storage panel ships none. SHARED_HYBRID_ITEMS exports one item each, and the tray carries a numbered socket per resting place, so a mesh is downloaded once however many end up on the shelf. Counts are what the tray physically holds, measured off the mesh; a width with no entry stages nothing.
const SHELF_GEAR: Record<string, ShelfGear> = {
  'medball shelf'        : { slot: 'mb', meshes: ['medball 20'], count: { 43: 3, 70: 4 } },
  'kettlebell shelf'     : { slot: 'kb', meshes: ['kettlebell'], count: { 43: 5, 70: 8 } },
  'dumbbell shelf'       : { slot: 'db', meshes: ['dumbbell'], count: { 43: 5, 70: 8 } },
  [HYBRID_DIVIDER_SHELF] : { slot: 'w', meshes: ['plate 45'], count: { 43: 12 } },
}

// Shelf, panel, bolt, wall-ball target, and chain-upright parts for HybridRigBuilder. getSideTransform/getUprightMesh are passed in bound rather than duplicated - both are pure functions of PowerRackBuilder's own config, and frontCrossmemberBuilder is the same delegate instance the main builder already constructed.
export class HybridShelfBuilder {
  protected config: RackOptions
  protected material: MaterialInfo
  protected frontCrossmemberBuilder: FrontCrossmemberBuilder
  protected getSideTransform: (left: boolean) => Partial<PartDefinition>
  protected getUprightMesh: () => string

  constructor(
    config: RackOptions,
    material: MaterialInfo,
    frontCrossmemberBuilder: FrontCrossmemberBuilder,
    getSideTransform: (left: boolean) => Partial<PartDefinition>,
    getUprightMesh: () => string
  ) {
    this.config = config
    this.material = material
    this.frontCrossmemberBuilder = frontCrossmemberBuilder
    this.getSideTransform = getSideTransform
    this.getUprightMesh = getUprightMesh
  }

  // No anchor (the export has none of its own) and no side transform (getSideTransform's 180 is AdjustOnlyVisual and never turns the slot frame - both sides' sockets already face front).
  createWallBallTarget(): PartDefinition {
    return {
      mesh      : getRackModel('SHARED_HYBRID_SHELVES', 'medball target'),
      materials : this.material,
      slots     : {},
    }
  }

  // MARK: Plate Storage
  addPlateStoragePins(upright: PartDefinition, left: boolean): void {
    // Takes any upright, unlike PowerRackBuilder.addStorageRow (six-post third row only, which Hybrid has none of). 'storage' would be shared with the back stabilizer, but Hybrid has no Stabilizer option, so it's always free here.
    if (!this.config.plateStorage) return

    // No plate arguments: the shared builder loads only its bottom two pins and splits one set into heavy and light rows, which is wrong here - both of those pins are long, and a long post takes 45s. Hybrid gets bare pins back and loads every one of them by type instead.
    const pins = addPlateStorage(
      this.config,
      left,
      this.material,
      this.getSideTransform(left),
      this.config.plateStorage
    )

    // Pin plates are staged by "View With Gear" like every other Hybrid gear item, never bought: config.plates is deliberately not consulted, so an Add plates option would not put them here either.
    if (this.config.showEquipped) this.loadPins(pins, left)

    upright.slots.storage = pins
  }

  // Pin type is read off the mesh the shared builder chose - the same two strings it passes to getRackModel - since the pins it returns carry nothing else to tell them apart.
  loadPins(pins: PartDefinition[], left: boolean): void {
    pins.forEach(pin => {
      // Short pins are the change-plate posts and stay bare: no 5/2.5/1.25 mesh exists yet, so anything staged here would be the wrong plate at the wrong size.
      if (pin.mesh.endsWith('short_plate_storage')) return

      pin.slots.plate_storage = this.createPinPlates(left)
    })
  }

  // Stacked here rather than through BarBuilder.createPlates so the pins use the same plate the shelves do. That builder is shared with every other rack and only ever reaches BARS, whose 45 carries neither the 45LB face nor the sleeve hole.
  createPinPlates(left: boolean): PartDefinition[] {
    // The starting x is the shared builder's own, kept so a Hybrid pin stacks exactly where an RM-6 one does.
    let offset = left ? 0 : 6.5

    return Array.from({ length: HYBRID_LONG_PIN_PLATE_COUNT }, () => {
      const plate: PartDefinition = {
        mesh   : getRackModel('SHARED_HYBRID_ITEMS', 'plate 45'),
        slots  : {},
        offset : { x: left ? -offset : offset, y: -HYBRID_PLATE_CENTER_RISE },
      }

      offset += HYBRID_PLATE_THICKNESS

      return plate
    })
  }

  // MARK: Shelves
  addShelves(backUpright: PartDefinition, shelves?: ShelfSelection, width?: string): void {
    // Mirrors caveBuilder.addShelves: shelf number maps 1:1 to slot, so Shelf 3 keeps its own socket whether or not 1 and 2 are filled.
    if (!shelves) return

    const selections = HYBRID_SHELF_KEYS.map(key => shelves[key])
    const offsets = computeShelfOffsets(selections, hybridShelfSpacing.value)

    selections.forEach((selection, index) => {
      if (!selection) return
      // Neither part is made at 70", so there is no mesh to load - the width rule in hybrid-rules hides both options, and this catches a selection left over from a width change. Same guard caveBuilder.addShelves carries.
      if ((selection === 'panel' || selection === HYBRID_DIVIDER_SHELF) && this.getShelfWidth(width) === '70') return

      backUpright.slots[HYBRID_SHELF_SLOTS[index]] = this.createShelf(selection, offsets[index], width)
    })
  }

  // Shelf meshes are suffixed by bare width ('medball 43' / 'medball 70'), but HybridUnit.width carries Cave's mass_width attribute verbatim, inch mark included.
  getShelfWidth(width?: string): string {
    return width?.replace(/\D/g, '') || '43'
  }

  // 'left' and 'right' are the shelf's two ends (x ±24.52 on medball 43), so anchoring on the far one lets the 180° turn the shelf back to front without changing which way it spans off the upright.
  createShelf(shelfName: string, offset: number, width?: string): PartDefinition {
    if (shelfName === 'panel') return this.createStoragePanel(offset, width)

    const shelfWidth = this.getShelfWidth(width)
    const overlay = SHELF_OVERLAY[shelfName]
    const gear = this.config.showEquipped ? SHELF_GEAR[shelfName] : undefined

    const slots: PartDefinition['slots'] = {
      bolts: this.createShelfPart(`hardware ${shelfWidth}`, this.config.line === 'RML' ? 'RML_HYBRID_HARDWARE' : 'RM_HYBRID_HARDWARE'),
    }

    if (overlay) slots[overlay] = this.createShelfPart(`${overlay} ${shelfWidth}`)

    // Sockets are numbered from one and sit on the tray itself, not on the kb/db cradle - a cradle is dropped into a tray slot at the tray's own origin, so the two frames coincide and one set of positions serves every shelf type.
    if (gear) {
      for (let i = 1; i <= (gear.count[shelfWidth] ?? 0); i += 1) {
        slots[`${gear.slot}${i}`] = this.createGear(gear.meshes[(i - 1) % gear.meshes.length])
      }
    }

    return {
      mesh        : getRackModel('SHARED_HYBRID_SHELVES', shelfName === HYBRID_DIVIDER_SHELF ? `bumper ${shelfWidth}` : `medball ${shelfWidth}`),
      materials   : this.material,
      anchor      : 'left',
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      offset      : { y: offset },
      slots,
    }
  }

  // A panel is a different part from the trays, not a variant of one: its own mesh, four bolt slots instead of one, and 'left'/'right' anchors sitting at the opposite ends from the trays' (x -24.26/+25.25 vs +24.52/-24.52 on medball 43). Both anchors sit at the same height, so anchoring the other end is a clean 180 turn - it spans the same way with no mirror, unlike a flip.
  createStoragePanel(offset: number, width?: string): PartDefinition {
    return {
      mesh      : getRackModel('SHARED_HYBRID_SHELVES', `panel ${this.getShelfWidth(width)}`),
      materials : this.material,
      anchor    : 'left',
      offset    : { x: -0.3, y: offset - 0.5 },
      slots     : {
        bolt1 : this.createPanelBolt(false),
        bolt2 : this.createPanelBolt(false),
        bolt3 : this.createPanelBolt(true),
        bolt4 : this.createPanelBolt(true),
      },
    }
  }

  // Neither single bolt carries an anchor of its own, so unlike CaveShelfBuilder.createRMShelfBolt the side only decides the nudge and mirror. The offsets below are authored against rm bolt's origin, which sits 1.32" off the bolt's centre along its axis - rml bolt has to be exported to that same origin or it lands shifted.
  createPanelBolt(left: boolean): PartDefinition {
    return {
      mesh: this.config.line === 'RML'
        ? getRackModel('RML_HYBRID_HARDWARE', 'rml bolt')
        : getRackModel('RM_HYBRID_HARDWARE', 'rm bolt'),
      materials   : this.material,
      slots       : {},
      offset      : { x: left ? 0.2 : -0.2 },
      adjustments : left ? [{ type: 'flip', axis: 'X' }] : [],
    }
  }

  // SHARED_HYBRID_ITEMS exports carry no anchor and no (mat|...) children, so gear keeps the colours it was authored with instead of taking the rack's finish, and lands on the slot origin unshifted.
  createGear(meshName: string): PartDefinition {
    return {
      mesh  : getRackModel('SHARED_HYBRID_ITEMS', meshName),
      slots : {},
    }
  }

  // Bolts and the kb/db overlays are all plain leaf meshes sitting at their slot's origin; only the bolts were split out into their own file, so the overlays keep the default.
  createShelfPart(meshName: string, modelKey = 'SHARED_HYBRID_SHELVES'): PartDefinition {
    return {
      mesh      : getRackModel(modelKey, meshName),
      materials : this.material,
      slots     : {},
    }
  }

  // MARK: Chain
  addChainFrontCrossmember(upright: PartDefinition, width: string | undefined, materials?: MaterialInfo): void {
    // Reuses PowerRackBuilder's own frontCrossmemberBuilder (config.front is hardcoded 'crossmember top 4' rig-wide) rather than a chain-specific mesh; materials are this rig's own Front Crossmember: pick, since the builder's shared config holds the rack's.
    const frontCrossmember = this.frontCrossmemberBuilder.createFrontCrossmember(width, materials)

    if (frontCrossmember) {
      upright.slots.top = frontCrossmember
    }
  }

  createChainUpright(left: boolean, anchor?: string): PartDefinition {
    return {
      mesh      : this.getUprightMesh(),
      materials : this.material,
      ...this.getSideTransform(left),
      ...(anchor ? { anchor } : {}),
      slots     : {},
    }
  }
}
